<script setup>
import { Head, Link, useForm } from "@inertiajs/inertia-vue3";
import Layout from "../../../Layouts/ManagementLayout.vue";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import Dropdown from "primevue/dropdown";
import MultiSelect from "primevue/multiselect";
import Checkbox from "primevue/checkbox";
import Message from "primevue/message";
import InputNumber from "primevue/inputnumber";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import Scheduler from "../../../Components/Management/Scheduler.vue";
import { computed, ref, watch, onMounted } from "vue";
import { usePage } from "@inertiajs/inertia-vue3";
import { useToast } from "primevue/usetoast";
import axios from "axios";
import ImageInput from "../../../Components/Management/ImageInput.vue";

const props = defineProps([
    'companies',
    'services',
    'flash',
]);

const route = window.route;
const toast = useToast();
const errors = ref({});
const filteredServices = ref(props.services);

const providerTypes = [
    { label: 'Person', value: 'person' },
    { label: 'Team', value: 'team' },
    { label: 'Virtual', value: 'virtual' },
];

const form = useForm({
    company_id: null,
    name: '',
    email: '',
    phone: '',
    provider_type: 'person',
    description: '',
    capacity: 1,
    is_active: true,
    visible_in_admin: true,
    visible_in_schedule: true,
    services: [], // Array of service IDs
    working_hours: [],
    image: null,
    sort_order: null,
});

const companyWorkingHours = ref(null);
const activeTab = ref(0);

onMounted(() => {
    const user = usePage().props.value.auth.user;
    if (user.role !== 'super_admin' && user.company_id) {
        form.company_id = user.company_id;
    }
});

const validateTab = (tabIndex) => {
    errors.value = {};

    if (tabIndex === 0) {
        if (!form.company_id) errors.value.company_id = 'Unternehmen ist erforderlich';
        if (!form.name) errors.value.name = 'Name ist erforderlich';
        if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            errors.value.email = 'Ungültiges E-Mail-Format';
        }
    }
    
    if (tabIndex === 2) {
        form.working_hours.forEach((h, idx) => {
            if (h.is_workday) {
                if (!h.start_time) errors.value[`working_hours.${idx}.start_time`] = 'Beginn ist erforderlich';
                if (!h.end_time) errors.value[`working_hours.${idx}.end_time`] = 'Ende ist erforderlich';
                if (h.start_time && h.end_time && h.start_time >= h.end_time) {
                    errors.value[`working_hours.${idx}.end_time`] = 'Ende muss nach Beginn liegen';
                }
                
                if (h.customer_start_time || h.customer_end_time) {
                    if (!h.customer_start_time) errors.value[`working_hours.${idx}.customer_start_time`] = 'Kunden-Beginn ist erforderlich';
                    if (!h.customer_end_time) errors.value[`working_hours.${idx}.customer_end_time`] = 'Kunden-Ende ist erforderlich';
                    if (h.customer_start_time && h.customer_end_time && h.customer_start_time >= h.customer_end_time) {
                        errors.value[`working_hours.${idx}.customer_end_time`] = 'Kunden-Ende muss nach Kunden-Beginn liegen';
                    }
                    if (h.customer_start_time && h.start_time && h.customer_start_time < h.start_time) {
                        errors.value[`working_hours.${idx}.customer_start_time`] = 'Kunden-Beginn darf nicht vor Mitarbeiter-Beginn liegen';
                    }
                    if (h.customer_end_time && h.end_time && h.customer_end_time > h.end_time) {
                        errors.value[`working_hours.${idx}.customer_end_time`] = 'Kunden-Ende darf nicht nach Mitarbeiter-Ende liegen';
                    }
                }
            }
        });
    }
    
    return Object.keys(errors.value).length === 0;
};

const nextTab = () => {
    if (validateTab(activeTab.value)) {
        activeTab.value++;
    }
};

const prevTab = () => {
    activeTab.value--;
};

// Update services list and working hours when company changes
watch(() => form.company_id, async (newId) => {
    if (newId) {
        // Fetch services
        try {
            const { data } = await axios.get(`/ajax/admin/services?company_id=${newId}&all=1`);
            filteredServices.value = data.data || data;
        } catch (e) {
            console.error("Failed to fetch services", e);
            filteredServices.value = [];
        }

        // Fetch company working hours
        try {
            const { data } = await axios.get(`/ajax/admin/company-working-hours?company_id=${newId}`);
            const records = data.data || [];
            companyWorkingHours.value = records;
            
            // Automatically populate provider working hours
            form.working_hours = records.map(h => ({
                day_of_week: h.day_of_week,
                is_workday: h.is_workday,
                start_time: h.start_time,
                end_time: h.end_time
            }));
        } catch (e) {
            console.error("Failed to fetch working hours", e);
            companyWorkingHours.value = null;
        }
    } else {
        filteredServices.value = props.services;
        companyWorkingHours.value = null;
        form.working_hours = [];
    }
});

const submit = () => {
    // Validate all tabs before final submission
    if (!validateTab(0)) {
        activeTab.value = 0;
        return;
    }
    if (!validateTab(1)) {
        activeTab.value = 1;
        return;
    }
    if (!validateTab(2)) {
        activeTab.value = 2;
        return;
    }

    form.post('/management/providers', {
        preserveState: true,
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Erfolgreich',
                detail: 'Mitarbeiter wurde erfolgreich gespeichert.',
                life: 5000,
            });
        },
        onError: (err) => {
            errors.value = err;
            
            // Auto-switch to the first tab with errors
            const errorKeys = Object.keys(err);
            const tab0Fields = ['company_id', 'name', 'email', 'phone', 'capacity', 'image', 'provider_type', 'description', 'is_active'];
            const tab1Fields = ['services'];
            
            if (errorKeys.some(key => tab0Fields.includes(key))) {
                activeTab.value = 0;
            } else if (errorKeys.some(key => tab1Fields.includes(key))) {
                activeTab.value = 1;
            } else {
                activeTab.value = 2;
            }

            toast.add({
                severity: 'error',
                summary: 'Fehler beim Speichern',
                detail: 'Bitte prüfen Sie die Eingaben in den markierten Feldern.',
                life: 5000,
            });
        }
    });
};

const hasErrors = computed(() => Object.values(errors.value).some(v => v));

</script>

<template>
    <Layout :flash="flash">
        <Head title="Mitarbeiter erstellen" />

        <div class="text-sm">
            <h1 class="text-2xl font-bold mb-8 text-gray-800">Neuen Mitarbeiter/Ressource erstellen</h1>

            <Message v-if="hasErrors" severity="error" class="mb-6">
                <div v-for="(error, key) in errors" :key="key">{{ error }}</div>
            </Message>

            <form @submit.prevent="submit" class="space-y-8" novalidate>
                <TabView v-model:activeIndex="activeTab">
                    <TabPanel header="1. Profil & Sichtbarkeit">
                        <div class="space-y-8 pt-4">
                            <!-- Section 1: Basic Profile -->
                            <div class="bg-gray-50/50 p-8 rounded-2xl border border-gray-100">
                                <div class="flex items-center gap-3 mb-6">
                                    <div class="w-10 h-10 rounded-full bg-black text-[#FFF053] flex items-center justify-center font-bold">1</div>
                                    <h2 class="text-lg font-bold text-gray-700">Profilinformationen</h2>
                                </div>
                                
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                                    <div v-if="$page.props.auth.user.role === 'super_admin'" class="flex flex-col gap-2">
                                        <label for="company_id" class="font-bold text-gray-600">Unternehmen *</label>
                                        <Dropdown id="company_id" v-model="form.company_id" :options="companies" optionLabel="name" optionValue="id" 
                                                placeholder="Unternehmen wählen" :class="{'p-invalid': errors.company_id}" filter class="w-full"
                                                @change="errors.company_id = null" />
                                        <small v-if="errors.company_id" class="p-error">{{ errors.company_id }}</small>
                                    </div>

                                    <div class="flex flex-col gap-2">
                                        <label for="provider_type" class="font-bold text-gray-600">Typ *</label>
                                        <Dropdown id="provider_type" v-model="form.provider_type" :options="providerTypes" optionLabel="label" optionValue="value" class="w-full" :class="{'p-invalid': errors.provider_type}" 
                                                  @change="errors.provider_type = null" />
                                        <small v-if="errors.provider_type" class="p-error">{{ errors.provider_type }}</small>
                                    </div>

                                    <div class="flex flex-col gap-2">
                                        <label for="name" class="font-bold text-gray-600">Name *</label>
                                        <InputText id="name" v-model="form.name" :class="{'p-invalid': errors.name}" class="w-full" placeholder="z.B. Max Mustermann" 
                                                   @input="errors.name = null" />
                                        <small v-if="errors.name" class="p-error">{{ errors.name }}</small>
                                    </div>

                                    <div class="flex flex-col gap-2">
                                        <label for="email" class="font-bold text-gray-600">E-Mail</label>
                                        <InputText id="email" type="email" v-model="form.email" :class="{'p-invalid': errors.email}" class="w-full" placeholder="max@vigory.ch" 
                                                   @input="errors.email = null" />
                                        <small v-if="errors.email" class="p-error">{{ errors.email }}</small>
                                    </div>

                                    <div class="flex flex-col gap-2">
                                        <label for="phone" class="font-bold text-gray-600">Telefon</label>
                                        <InputText id="phone" v-model="form.phone" class="w-full" :class="{'p-invalid': errors.phone}" 
                                                   @input="errors.phone = null" />
                                        <small v-if="errors.phone" class="p-error">{{ errors.phone }}</small>
                                    </div>

                                    <div class="flex flex-col gap-2">
                                        <label for="capacity" class="font-bold text-gray-600">Kapazität (Gleichzeitige Buchungen)</label>
                                        <InputNumber id="capacity" v-model="form.capacity" :min="1" class="w-full" inputClass="w-full" showButtons :class="{'p-invalid': errors.capacity}" 
                                                     @input="errors.capacity = null" />
                                        <small v-if="errors.capacity" class="p-error">{{ errors.capacity }}</small>
                                    </div>

                                    <div class="flex flex-col gap-2">
                                        <label for="sort_order" class="font-bold text-gray-600">Sortierreihenfolge</label>
                                        <InputNumber id="sort_order" v-model="form.sort_order" class="w-full" inputClass="w-full" showButtons :class="{'p-invalid': errors.sort_order}" 
                                                     @input="errors.sort_order = null" />
                                        <small v-if="errors.sort_order" class="p-error">{{ errors.sort_order }}</small>
                                    </div>

                                    <div class="flex flex-col gap-2">
                                        <ImageInput v-model="form.image" label="Bild" :error="errors.image" />
                                    </div>
                                </div>
                            </div>

                            <!-- Section 3: Visibility -->
                            <div class="bg-gray-50/50 p-8 rounded-2xl border border-gray-100">
                                <div class="flex items-center gap-3 mb-6">
                                    <div class="w-10 h-10 rounded-full bg-black text-[#FFF053] flex items-center justify-center font-bold">2</div>
                                    <h2 class="text-lg font-bold text-gray-700">Sichtbarkeit & Beschreibung</h2>
                                </div>
                                
                                <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                    <div class="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                                        <Checkbox id="is_active" v-model="form.is_active" :binary="true" />
                                        <label for="is_active" class="font-bold text-gray-700 cursor-pointer">Aktiv</label>
                                    </div>
                                    <div class="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                                        <Checkbox id="visible_in_admin" v-model="form.visible_in_admin" :binary="true" />
                                        <label for="visible_in_admin" class="font-bold text-gray-700 cursor-pointer">Sichtbar in Admin</label>
                                    </div>
                                    <div class="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                                        <Checkbox id="visible_in_schedule" v-model="form.visible_in_schedule" :binary="true" />
                                        <label for="visible_in_schedule" class="font-bold text-gray-700 cursor-pointer">Sichtbar im Kalender</label>
                                    </div>
                                </div>

                                <div class="flex flex-col gap-2">
                                    <label for="description" class="font-bold text-gray-600">Beschreibung</label>
                                    <Textarea id="description" v-model="form.description" rows="4" class="w-full" />
                                </div>
                            </div>
                        </div>
                    </TabPanel>

                    <TabPanel header="2. Dienstleistungen">
                        <div class="pt-4">
                            <!-- Section 2: Services -->
                            <div class="bg-gray-50/50 p-8 rounded-2xl border border-gray-100">
                                <div class="flex items-center gap-3 mb-6">
                                    <div class="w-10 h-10 rounded-full bg-black text-[#FFF053] flex items-center justify-center font-bold">3</div>
                                    <h2 class="text-lg font-bold text-gray-700">Zugeordnete Dienstleistungen</h2>
                                </div>
                                
                                <div class="flex flex-col gap-2">
                                    <label for="services" class="font-bold text-gray-600">Angebotene Dienstleistungen</label>
                                    <MultiSelect id="services" v-model="form.services" :options="filteredServices" optionLabel="name" optionValue="id" 
                                                placeholder="Dienstleistungen auswählen" filter class="w-full max-w-full" :class="{'p-invalid': errors.services}" 
                                                :maxSelectedLabels="2" :selectedItemsLabel="form.services.length + ' Dienstleistungen ausgewählt'"
                                                @change="errors.services = null" />
                                    <small v-if="errors.services" class="p-error">{{ errors.services }}</small>
                                    <p class="text-xs text-gray-400 mt-1" v-if="!form.company_id">Bitte wählen Sie zuerst ein Unternehmen aus.</p>
                                </div>
                            </div>
                        </div>
                    </TabPanel>

                    <TabPanel header="3. Arbeitszeiten">
                        <div class="pt-4">
                            <div v-if="!form.company_id" class="p-8 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                                <i class="pi pi-building text-4xl text-gray-300 mb-4" />
                                <h3 class="text-lg font-bold text-gray-500">Unternehmen wählen</h3>
                                <p class="text-gray-400">Bitte wählen Sie ein Unternehmen aus, um die Arbeitszeiten festzulegen.</p>
                            </div>
                            <Scheduler 
                                v-else 
                                v-model="form.working_hours" 
                                title="Mitarbeiter Arbeitszeiten" 
                                :show-customer-hours="true"
                                :companyHours="companyWorkingHours" 
                                :errors="errors"
                                @clear-error="(key) => errors[key] = null"
                            />
                            <p class="text-xs text-gray-400 mt-4 px-4" v-if="form.company_id">
                                <i class="pi pi-info-circle mr-1" />
                                Die Arbeitszeiten des Mitarbeiters können nicht außerhalb der Unternehmensöffnungszeiten liegen.
                            </p>
                        </div>
                    </TabPanel>
                </TabView>

                <!-- Unified Footer Actions -->
                <div class="flex justify-between items-center pt-8 border-t mt-10">
                    <!-- Left: Always Abbrechen -->
                    <Button asChild v-slot="slotProps" severity="secondary" outlined>
                        <Link href="/management/providers" :class="slotProps.class" class="px-8 py-3 rounded-xl border-gray-300 font-bold">
                            <i class="pi pi-times mr-2" />
                            Abbrechen
                        </Link>
                    </Button>

                    <!-- Right: Navigation & Submit -->
                    <div class="flex items-center gap-4">
                        <Button v-if="activeTab > 0" type="button" label="Zurück" icon="pi pi-arrow-left" severity="secondary" text @click="prevTab" />
                        
                        <Button v-if="activeTab < 2" type="button" 
                                :label="activeTab === 0 ? 'Weiter zu Dienstleistungen' : 'Weiter zu Arbeitszeiten'" 
                                icon="pi pi-arrow-right" iconPos="right" 
                                class="!bg-black !text-[#FFF053] !border-none px-8 py-3 rounded-xl font-bold shadow-sm" 
                                @click="nextTab" />
                        
                        <Button v-if="activeTab === 2" type="submit" 
                                class="!bg-black !text-[#FFF053] !border-none px-10 py-3 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-gray-200" 
                                :loading="form.processing" :disabled="form.processing">
                            <i class="pi pi-save" />
                            Mitarbeiter speichern
                        </Button>
                    </div>
                </div>
            </form>
        </div>
    </Layout>
</template>
